import { useEffect } from 'react';
import { AppDispatch, useAppDispatch, useAppSelector } from '../../store';
import { resourceActions } from '../resources/slice';
import { Resource } from '../resources/types';
import { themeActions } from '../themes/slice';
import { Theme } from '../themes/types';
import { SyncResponseAction, SyncState } from './types';
import { makeSyncRequest } from './api';
import { syncActions } from './slice';
import { Account } from '../auth/types';
import { useAccount } from '../account/hooks';

const MINUTES = 60 * 1000;

export const handleSyncResponse = (actions: SyncResponseAction[], dispatch: AppDispatch) => {
  const resources: Record<string, Resource> = {};
  const deletedResources = [];
  const themes: Record<string, Theme> = {};
  const deletedThemes = [];

  for (const action of actions) {
    switch(action.type) {
      case 'resource/DELETE':
        deletedResources.push((action.resource || action.payload).id);
        break;
      case 'theme/DELETE':
        deletedThemes.push((action.theme || action.payload).id);
        break;
      case 'resource/PUT': {
          // Some (old?) data uses `payload` instead of `resource` - I think
          // it's junk data from the early days.
          const resource = (action.resource || action.payload);
          resources[resource.id] = resource;
        break;
      }
      case 'theme/PUT': {
        const theme = action.theme ||  action.payload;
        themes[theme.id] = theme;
        break;
      }
    }
  }

  if (Object.keys(resources).length > 0) {
    dispatch(resourceActions.bulkImport(resources));
  }
  if (deletedResources.length > 0) {
    dispatch(resourceActions.bulkDelete(deletedResources));
  }
  if (Object.keys(themes).length > 0) {
    dispatch(themeActions.bulkImport(themes));
  }
  if (deletedThemes.length > 0) {
    dispatch(themeActions.bulkDelete(deletedThemes));
  }
};

let syncInProgress = false;

export const doSync = async (
  account: Account | undefined,
  syncState: SyncState,
  dispatch: AppDispatch
) => {
  const accountID = account?.id;
  if (accountID && !syncInProgress && account?.permissions?.includes("SYNC")) {
    syncInProgress = true;
    dispatch(syncActions.setLastAttempt(accountID));
    const response = await makeSyncRequest(
      syncState[accountID]?.queue,
      syncState[accountID]?.lastSuccess || 0
    );
    handleSyncResponse(response.actions, dispatch);
    dispatch(syncActions.clearQueue(accountID));
    dispatch(syncActions.setLastSuccess(accountID));
    syncInProgress = false;
  }
};

export const usePeriodicSync = (every: number = 5 * MINUTES) => {
  const dispatch = useAppDispatch();
  const account = useAccount();
  const syncState = useAppSelector(state => state.sync);

  useEffect(
    () => {
      doSync(account, syncState, dispatch);
    },
    // Deliberately ignoring syncState as we don't want to rerun sync every time
    // that state changes!
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [account, dispatch]
  );

  useEffect(
    () => {
      const interval = setInterval(
        doSync,
        every,
        account,
        syncState,
        dispatch
      );
      return () => {
        if (interval) {
          clearInterval(interval);
        }
      };
    },
    [account, dispatch, every, syncState]
  );
};
